import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { glossary } from '@sorare/core/src/lib/glossary';
import type { WithRewardRange } from '@sorare/core/src/lib/rewards';

import { LineupRewardsDistanceLabel } from 'components/lineup/LineupRewardsDistanceLabel';

import { ColoredRanking } from './ColoredRanking';

const BrandSup = styled(BrandXXS).attrs({ as: 'sup', bold: true })``;

type Props = WithRewardRange<object> & {
  scoreDistanceFromMyRanking?: number;
  hideScoreDistance?: boolean;
};

export const RankRewardTitle = ({
  startRank,
  endRank,
  startPct,
  endPct,
  scoreDistanceFromMyRanking,
  hideScoreDistance,
}: Props) => {
  const start = startRank ?? (startPct || 0);
  const end = endRank ?? (endPct || 0);

  return (
    <Vertical gap={0.5}>
      {start === end ? (
        <Horizontal>
          <TrophyIcon ranking={start} />
          <ColoredRanking className={`rank-${start}`}>
            <Ordinal ordinal={start} Sup={BrandSup} />
          </ColoredRanking>
        </Horizontal>
      ) : (
        <Horizontal gap={0.5}>
          <ColoredRanking>
            {startRank ? (
              <Ordinal ordinal={start} Sup={BrandSup} />
            ) : (
              <FormattedMessage
                {...glossary.percentWithSup}
                values={{
                  sup: c => <BrandSup>{c}</BrandSup>,
                  percent: start * 100,
                }}
              />
            )}
          </ColoredRanking>
          <ColoredRanking>-</ColoredRanking>
          <ColoredRanking>
            {endRank ? (
              <Ordinal ordinal={end} Sup={BrandSup} />
            ) : (
              <FormattedMessage
                {...glossary.percentWithSup}
                values={{
                  sup: c => <BrandSup>{c}</BrandSup>,
                  percent: end * 100,
                }}
              />
            )}
          </ColoredRanking>
        </Horizontal>
      )}
      {!!scoreDistanceFromMyRanking && !hideScoreDistance && (
        <LabelS>
          <LineupRewardsDistanceLabel
            distance={scoreDistanceFromMyRanking}
            withFromAppendix={false}
          />
        </LabelS>
      )}
    </Vertical>
  );
};
